import { useEffect, useRef, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { compressImage } from "../utils/image";
import { carName, carPhotoUrl } from "./CarCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { make: "", model: "", year: "", mileage_km: "", fuel: "petrol", transmission: "automatic", price_aed: "", price_eur: "", location: "dubai", status: "available", video_url: "", inspection_summary: "" };

export default function CarsAdmin() {
  const { t } = useLang();
  const n = t("nx.cars");
  const [cars, setCars] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [openCarId, setOpenCarId] = useState(null);
  const fileRef = useRef(null);

  const load = () => fetch(`${API}/cars`, { credentials: "include" }).then((r) => (r.ok ? r.json() : Promise.reject())).then(setCars).catch(() => setCars([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    const payload = {
      ...form,
      year: Number(form.year), mileage_km: Number(form.mileage_km),
      price_aed: Number(form.price_aed), price_eur: Number(form.price_eur),
    };
    try {
      const res = await fetch(editingId ? `${API}/cars/${editingId}` : `${API}/cars`, {
        method: editingId ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const car = await res.json();
      setForm(EMPTY); setEditingId(null); setOpenCarId(car.id);
      await load();
      setMsg(editingId ? t("sell.admin.saved") || "Saved." : "Saved.");
    } catch {
      setMsg("Error — check the fields.");
    }
    setBusy(false);
  };

  const edit = (car) => {
    setEditingId(car.id);
    setOpenCarId(car.id);
    setForm({
      make: car.make, model: car.model, year: String(car.year), mileage_km: String(car.mileage_km),
      fuel: car.fuel, transmission: car.transmission, price_aed: String(car.price_aed), price_eur: String(car.price_eur),
      location: car.location, status: car.status, video_url: car.video_url || "", inspection_summary: car.inspection_summary || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (car) => {
    if (!window.confirm(`Delete ${carName(car)}? This cannot be undone.`)) return;
    await fetch(`${API}/cars/${car.id}`, { method: "DELETE", credentials: "include" });
    if (editingId === car.id) { setEditingId(null); setForm(EMPTY); }
    load();
  };

  const uploadPhotos = async (carId, fileList) => {
    const files = Array.from(fileList || []).slice(0, 150);
    for (const raw of files) {
      const processed = await compressImage(raw);
      const fd = new FormData();
      fd.append("file", processed);
      await fetch(`${API}/cars/${carId}/photos`, { method: "POST", credentials: "include", body: fd });
    }
    load();
  };

  const removePhoto = async (carId, pid) => {
    await fetch(`${API}/cars/${carId}/photos/${pid}`, { method: "DELETE", credentials: "include" });
    load();
  };

  const inp = (key, label, type = "text", testidExtra = "") => (
    <div data-testid={`field-car-${key}`}>
      <label className="field-label" htmlFor={`car-${key}`}>{label}</label>
      <input id={`car-${key}`} type={type} className="field-input" data-testid={`input-car-${key}${testidExtra}`}
        value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );
  const sel = (key, label, options) => (
    <div data-testid={`field-car-${key}`}>
      <label className="field-label" htmlFor={`car-${key}`}>{label}</label>
      <select id={`car-${key}`} className="field-input cursor-pointer" data-testid={`input-car-${key}`}
        value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <div data-testid="admin-cars">
      <form onSubmit={save} className="border border-white/10 p-6 md:p-8 mb-10" data-testid="car-admin-form">
        <div className="eyebrow mb-6">{editingId ? n.adminEdit : n.adminAdd}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
          {inp("make", n.f_make)}{inp("model", n.f_model)}{inp("year", n.f_year, "number")}
          {inp("mileage_km", n.f_mileage, "number")}
          {sel("fuel", n.f_fuel, [["petrol", "PETROL"], ["diesel", "DIESEL"], ["hybrid", "HYBRID"], ["plugin_hybrid", "PLUG-IN HYBRID"], ["electric", "ELECTRIC"], ["other", "OTHER"]])}
          {sel("transmission", n.f_transmission, [["automatic", "AUTOMATIC"], ["manual", "MANUAL"], ["other", "OTHER"]])}
          {inp("price_aed", n.f_priceAed, "number")}{inp("price_eur", n.f_priceEur, "number")}
          {sel("location", n.f_location, [["dubai", n.locationLabels.dubai], ["in_transit", n.locationLabels.in_transit]])}
          {sel("status", n.f_status, [["available", n.statusLabels.available], ["reserved", n.statusLabels.reserved], ["sold", n.statusLabels.sold]])}
          <div className="md:col-span-3" data-testid="field-car-video_url">
            <label className="field-label" htmlFor="car-video_url">{n.f_video}</label>
            <input id="car-video_url" className="field-input" placeholder="https://www.youtube.com/watch?v=…" data-testid="input-car-video_url"
              value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
          </div>
          <div className="md:col-span-3" data-testid="field-car-inspection_summary">
            <label className="field-label" htmlFor="car-inspection_summary">{n.f_inspection}</label>
            <textarea id="car-inspection_summary" rows={4} className="field-input" data-testid="input-car-inspection_summary"
              value={form.inspection_summary} onChange={(e) => setForm({ ...form, inspection_summary: e.target.value })} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <button type="submit" disabled={busy} className="btn btn-solid !py-2.5 !px-6" data-testid="car-save-btn">
            {editingId ? n.adminUpdate : n.adminCreate}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="btn !py-2.5 !px-6" data-testid="car-cancel-btn">{n.adminCancel}</button>
          )}
          {msg && <span className="mono text-[11px] text-[#999995] uppercase tracking-[0.14em]" data-testid="car-admin-msg">{msg}</span>}
        </div>
      </form>

      {cars === null && <p className="mono text-[12px] text-[#666663]">…</p>}
      {cars && cars.length === 0 && <p className="mono text-[12px] text-[#666663]" data-testid="cars-admin-empty">{n.adminNone}</p>}
      {(cars || []).map((car) => (
        <div key={car.id} className="border border-white/10 mb-4" data-testid="car-admin-row">
          <div className="flex flex-col md:flex-row md:items-center gap-4 p-4">
            <img src={carPhotoUrl(car)} alt={carName(car)} width="96" height="60" loading="lazy" className="w-24 h-15 object-cover border border-white/10" />
            <div className="grow">
              <div className="mono text-[12px] text-[#F3F3F0] uppercase tracking-[0.14em]">{carName(car)}</div>
              <div className="mono text-[10px] text-[#666663] mt-1">{Number(car.price_aed).toLocaleString()} AED · {Number(car.price_eur).toLocaleString()} EUR · {(car.photo_ids || []).length} {n.photosWord}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(car)} className="btn !py-2 !px-4" data-testid={`car-edit-${car.id}`}>{n.adminEdit}</button>
              <button onClick={() => remove(car)} className="btn !py-2 !px-4" data-testid={`car-delete-${car.id}`}>{n.adminDelete}</button>
              <button onClick={() => setOpenCarId(openCarId === car.id ? null : car.id)} className="btn btn-solid !py-2 !px-4" data-testid={`car-photos-toggle-${car.id}`}>
                {n.adminPhotos} ({(car.photo_ids || []).length})
              </button>
            </div>
          </div>
          {openCarId === car.id && (
            <div className="border-t border-white/10 p-4" data-testid="car-photo-manager">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
                onChange={(e) => { uploadPhotos(car.id, e.target.files); e.target.value = ""; }} data-testid="car-photo-input" />
              <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="btn btn-solid !py-2 !px-5 mb-4" data-testid="car-photo-add-btn">
                {n.adminPhotosAdd} ({(car.photo_ids || []).length}/150)
              </button>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {(car.photo_ids || []).map((pid, i) => (
                  <div key={pid} className="relative group" data-testid={`car-photo-item-${i}`}>
                    <img src={`${API}/cars/${car.id}/photos/${pid}`} alt={`${carName(car)} ${i + 1}`} width="160" height="100" loading="lazy" className="w-full h-20 object-cover border border-white/10" />
                    <button type="button" onClick={() => removePhoto(car.id, pid)} aria-label="Delete photo"
                      className="absolute top-1 right-1 w-6 h-6 bg-[#050607]/85 border border-white/20 text-[#F3F3F0] text-[11px] cursor-pointer" data-testid={`car-photo-delete-${i}`}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
